import {Component, inject, OnInit} from '@angular/core';
import {ConfigOverviewModel} from "../../../shared/components/models/config-overview.model";
import {ConfigCardModel} from "../../../shared/models/config-card.model";
import {ConfigTableDisplayedColumns} from "../../../shared/components/models/config-table.model";
import {Router} from "@angular/router";
import {ProgressService} from "../../../shared/services/progress.service";
import {SnackbarService} from "../../../shared/services/snackbar.service";
import {finalize, map, of, tap} from "rxjs";
import {catchError} from "rxjs/operators";
import {QueryDataService} from "../services/query-data.service";
import {QueryOverview} from "../models/query.model";
import {ConfigRouteEnum} from '../../../shared/enums/config-route.enum';
import {TranslateService} from "@ngx-translate/core";
import {ConfigButton} from "../../../shared/components/models/config-button";
import {ConfigOverviewComponent} from "../../../shared/components/config-overview/config-overview.component";
import {
    ConfigAmountBadgesComponent
} from "../../../shared/components/config-amount-badges/config-amount-badges.component";

@Component({
    selector: 'app-query',
    imports: [
        ConfigOverviewComponent,
    ],
    templateUrl: './query.component.html',
    styleUrl: './query.component.scss'
})
export class QueryComponent implements OnInit {
    queryModel?: ConfigOverviewModel;

    private readonly _router: Router = inject(Router);

    private allCards: ConfigCardModel[] = [];
    private displayedColumns: any[] = [];

    private products: QueryOverview[] = [];

    private readonly queryDataService = inject(QueryDataService);
    private readonly progress = inject(ProgressService);
    private readonly snackBar = inject(SnackbarService);
    private readonly router = inject(Router);
    protected readonly ConfigRouteEnum = ConfigRouteEnum;
    private readonly translate = inject(TranslateService);

    ngOnInit(): void {
        this.loadQueries();
    }

    private loadQueries(): void {
        this.progress.startLoading();

        this.queryDataService
            .getQueries()
            .pipe(
                map((queries: any) => {
                    const queryList = queries._embedded?.query || [];
                    const applicationNames = new Map<string, any[]>();

                    queryList.forEach((query: any) => {
                        const appName = query.application.name;
                        if (!applicationNames.has(appName)) {
                            applicationNames.set(appName, []);
                        }
                        applicationNames.get(appName)?.push(query);
                    });

                    this.products = Array.from(applicationNames.keys()).map(name => {
                        const queriesInApp = applicationNames.get(name) || [];

                        const newestQueriesByName = new Map<string, any>();
                        queriesInApp.forEach((query: any) => {
                            const existing = newestQueriesByName.get(query.name);
                            if (!existing || this.compareVersions(query.version, existing.version) > 0) {
                                newestQueriesByName.set(query.name, query);
                            }
                        });

                        return {
                            name: name,
                            searchName: name,
                            amount: newestQueriesByName.size
                        } as QueryOverview;
                    });
                }),
                tap(() => this.buildOverview()),
                catchError(() => {
                    this.snackBar.Error(this.translate.instant("snackbar.query.error"));
                    this.products = [];
                    this.buildOverview();
                    return of([]);
                }),
                finalize(() => this.progress.stopLoading())
            )
            .subscribe();
    }

    private compareVersions(v1: string, v2: string): number {
        const parts1 = (v1 || '').split('.').map(Number);
        const parts2 = (v2 || '').split('.').map(Number);
        const maxLength = Math.max(parts1.length, parts2.length);

        for (let i = 0; i < maxLength; i++) {
            const p1 = parts1[i] || 0;
            const p2 = parts2[i] || 0;
            if (p1 > p2) return 1;
            if (p1 < p2) return -1;
        }
        return 0;
    }

    private buildOverview(): void {
        this.allCards = this.products.map(
            (t, id) =>
                new ConfigCardModel({
                    id: id,
                    title: t.name,
                    searchName: t.searchName,
                    body: null,
                    logoUrl: null,
                    route: {
                        commands: [ConfigRouteEnum.QUERY, t.searchName],
                    },
                    badges: [
                        {amount: (t.amount ?? 0).toString(), label: 'query.queries', routerLink: undefined}
                    ],
                })
        );

        this.allCards.forEach(card => {
            (card as any).ConfigBadgesComponent = {badges: card.badges, direction: 'row'};
        });

        this.displayedColumns = ConfigTableDisplayedColumns.build(
            {HeaderCellName: 'query.applications', PropertyName: 'title'},
            {HeaderCellName: '', PropertyName: 'ConfigBadgesComponent', Component: ConfigAmountBadgesComponent},
        );

        this.queryModel = {
            title: 'query.queries',
            buttons: ConfigButton.build({
                text: 'query.create',
                icon: 'pi pi-plus',
                onClick: async () => await this.router.navigate(['/', ConfigRouteEnum.QUERY, 'new'])
            }),
            cards: this.allCards,
            table: {
                settings: {
                    header: true,
                    headerAction: true,
                    search: true,
                },
                data: this.allCards,
                displayedColumns: this.displayedColumns,
                onRowClick: (row: any) => this._router.navigate(row.route.commands, row.route.extras),
            },
        };
    }
}
